import React, { type CSSProperties } from 'react';
import {
  ArrowClockwise,
  ArrowCounterclockwise,
  BracesAsterisk,
  Clipboard,
  FiletypeHtml,
  Highlighter,
  Hr,
  Link45deg,
  ListCheck,
  ListOl,
  ListUl,
  MenuButton,
  MenuButtonFill,
  MenuButtonWideFill,
  Paragraph,
  Quote,
  Superscript,
  Table,
  TagFill,
  TextIndentLeft,
  TextIndentRight,
  TypeBold,
  TypeH1,
  TypeH2,
  TypeH3,
  TypeItalic,
  TypeStrikethrough,
  TypeUnderline,
} from 'react-bootstrap-icons';
import { type IconProps, type IIconProps } from '../dm/icons.js';
import { RichTextIcon } from './svg/RichText.js';
import { SourceIcon } from './svg/Source.js';
import { FootnoteIcon } from './svg/Footnote.js';
import { SmallCapsIcon } from './svg/SmallCaps.js';
import { AllUpperIcon } from './svg/AllUpper.js';
import { EraIcon } from './svg/Era.js';
import { BibleLinkIcon } from './svg/BibleLinkIcon.js';
import { EmbedPassageIcon } from './svg/EmbedPassageIcon.js';

export const editorIcons = [
  'undo',
  'redo',
  'bold',
  'italic',
  'underline',
  'highlight',
  'strikethrough',
  'edit-richtext',
  'edit-source',
  'link',
  'superscript',
  'paragraph',
  'h1',
  'h2',
  'h3',
  'bullet',
  'check',
  'number',
  'smallcaps',
  'era',
  'biblelink',
  'embed-passage',
  'toolbar-full',
  'toolbar-mini',
  'toolbar-off',
  'code',
  'allupper',
  'shx-quote',
  'paste-quote',
  'edit-quote-citation',
  'export-html',
  'footnote',
  'horizontal-rule',
  'table',
  'indent',
  'outdent',
];

export type EditorIconType = (typeof editorIcons)[number];

interface IEditorIcons extends IconProps {
  type: EditorIconType;
  style?: CSSProperties;
}

export const EditorIcons = ({ type, size = '1em', color = 'currentColor', className, style }: IEditorIcons) => {
  const props: IIconProps = { size, color, className, style };

  switch (type) {
    case 'undo': {
      return <ArrowCounterclockwise {...props} />;
    }
    case 'redo': {
      return <ArrowClockwise {...props} />;
    }
    case 'bold': {
      return <TypeBold {...props} />;
    }
    case 'italic': {
      return <TypeItalic {...props} />;
    }
    case 'underline': {
      return <TypeUnderline {...props} />;
    }
    case 'strikethrough': {
      return <TypeStrikethrough {...props} />;
    }
    case 'edit-richtext': {
      return <RichTextIcon {...props} />;
    }
    case 'edit-source': {
      return <SourceIcon {...props} />;
    }
    case 'highlight': {
      return <Highlighter {...props} />;
    }
    case 'link': {
      return <Link45deg {...props} />;
    }
    case 'superscript': {
      return <Superscript {...props} />;
    }
    case 'footnote': {
      return <FootnoteIcon {...props} />;
    }
    case 'paragraph': {
      return <Paragraph {...props} />;
    }
    case 'h1': {
      return <TypeH1 {...props} />;
    }
    case 'h2': {
      return <TypeH2 {...props} />;
    }
    case 'h3': {
      return <TypeH3 {...props} />;
    }
    case 'bullet': {
      return <ListUl {...props} />;
    }
    case 'check': {
      return <ListCheck {...props} />;
    }
    case 'number': {
      return <ListOl {...props} />;
    }
    case 'smallcaps': {
      return <SmallCapsIcon {...props} />;
    }
    case 'allupper': {
      return <AllUpperIcon {...props} />;
    }
    case 'era': {
      return <EraIcon {...props} />;
    }
    case 'biblelink': {
      return <BibleLinkIcon {...props} />;
    }
    case 'embed-passage': {
      return <EmbedPassageIcon {...props} />;
    }
    case 'toolbar-full': {
      return <MenuButtonWideFill {...props} />;
    }
    case 'toolbar-mini': {
      return <MenuButtonFill {...props} />;
    }
    case 'toolbar-off': {
      return <MenuButton {...props} />;
    }
    case 'code': {
      return <BracesAsterisk {...props} />;
    }
    case 'shx-quote': {
      return <Quote {...props} />;
    }
    case 'paste-quote': {
      return <Clipboard {...props} />;
    }
    case 'edit-quote-citation': {
      return <TagFill {...props} />;
    }
    case 'export-html': {
      return <FiletypeHtml {...props} />;
    }
    case 'horizontal-rule': {
      return <Hr {...props} />;
    }
    case 'table': {
      return <Table {...props} />;
    }
    case 'indent': {
      return <TextIndentLeft {...props} />;
    }
    case 'outdent': {
      return <TextIndentRight {...props} />;
    }
    default: {
      return null;
    }
  }
};
